/**
 * App-wide toast system (2026-06-10, internal-cloud UI/UX pass).
 *
 * WHY: @radix-ui/react-toast has been in the dependency graph since Phase 3
 * but no shared toast surface ever shipped; failure feedback drifted into
 * native `window.alert()` (SiteDetailHeader), silent `console.warn`s
 * (SiteConfigTab's Open Local Dashboard), and ad-hoc inline status regions.
 * This module gives the SPA ONE on-system notification primitive so every
 * action result speaks the same visual language (SOC card, tokenized
 * motion, reduced-motion-safe, screen-reader announced via Radix's
 * built-in aria-live region).
 *
 * USAGE:
 *   const { toast } = useToast();
 *   toast({ title: 'Credentials saved', variant: 'success' });
 *   toast({ title: 'Mint failed', description: err.message, variant: 'error' });
 *
 * The provider is mounted once in main.tsx (inside ThemeProvider so theme
 * vars resolve, outside the router so toasts survive route rebuilds).
 * Components without a provider above them (isolated unit tests) get a
 * console-warn no-op instead of a crash - notifications must never take
 * the app down.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import * as styles from './Toast.css';

/** Severity variants mapped to the SOC status palette. */
export type ToastVariant = 'info' | 'success' | 'error';

/** Options accepted by the `toast()` imperative API. */
export interface ToastOptions {
  /** Short, uppercase-styled headline (required). */
  title: string;
  /** Optional secondary line with detail (e.g. an error message). */
  description?: string;
  /** Visual severity; defaults to 'info' (gold edge). */
  variant?: ToastVariant;
  /** Auto-dismiss window in ms; defaults to TOAST_DEFAULT_DURATION_MS. */
  durationMs?: number;
}

/** Default auto-dismiss window (ms). Long enough to read two lines. */
export const TOAST_DEFAULT_DURATION_MS = 5_000;

/** Internal queued-toast record (options + identity for React keys). */
interface ToastRecord extends Required<Omit<ToastOptions, 'description'>> {
  id: number;
  description?: string;
}

/** Context value: the imperative `toast` dispatcher. */
interface ToastContextValue {
  toast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Monotonic id source for toast React keys (module scope = stable). */
let nextToastId = 1;

/**
 * ToastProvider - mounts the Radix provider + viewport and exposes the
 * imperative `toast()` dispatcher via context.
 *
 * @param props.children Subtree that may call useToast().
 * @returns Provider-wrapped children plus the fixed toast viewport.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  /** Queue a toast for display. Stable identity via useCallback. */
  const toast = useCallback((options: ToastOptions): void => {
    const record: ToastRecord = {
      id: nextToastId++,
      title: options.title,
      description: options.description,
      variant: options.variant ?? 'info',
      durationMs: options.durationMs ?? TOAST_DEFAULT_DURATION_MS,
    };
    setToasts((prev) => [...prev, record]);
  }, []);

  /** Drop a toast from state once Radix reports it fully closed. */
  const handleOpenChange = useCallback((id: number, open: boolean): void => {
    if (!open) {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }
  }, []);

  const contextValue = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={contextValue}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => (
          <RadixToast.Root
            key={t.id}
            className={`${styles.root} ${styles.variantEdge[t.variant]}`}
            duration={t.durationMs}
            onOpenChange={(open) => handleOpenChange(t.id, open)}
            data-testid={`toast-${t.variant}`}
          >
            <div className={styles.textColumn}>
              <RadixToast.Title className={styles.title}>{t.title}</RadixToast.Title>
              {t.description ? (
                <RadixToast.Description className={styles.description}>
                  {t.description}
                </RadixToast.Description>
              ) : null}
            </div>
            <RadixToast.Close asChild>
              <button
                type="button"
                className={styles.closeButton}
                aria-label="Dismiss notification"
                data-tap-target-ignore
              >
                &times;
              </button>
            </RadixToast.Close>
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className={styles.viewport} label="Notifications" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

/**
 * useToast - read the imperative toast dispatcher.
 *
 * Defensive: when no ToastProvider is mounted (isolated component unit
 * tests, storybook-style harnesses), returns a console-warn no-op instead
 * of throwing. A missed notification is strictly better than a crash on a
 * failure-feedback path.
 *
 * @returns `{ toast }` dispatcher.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;
  return {
    toast: (options: ToastOptions) => {
      // eslint-disable-next-line no-console -- visible signal in harnesses without a provider
      console.warn('[toast] no ToastProvider mounted:', options.title);
    },
  };
}
